import React, { useEffect, useState } from 'react';
import { FaCheck, FaExpand, FaSignOutAlt } from 'react-icons/fa';
import { GiTransportationRings, GiRingedBeam } from "react-icons/gi";
import {
  SliderContainer,
  FineButton,
  ValueLabel,
  MiniButton,
  StyledSlider,
} from '../styled';
import { toggleFullscreen } from '../functions/toggleFullscreen';
import { gameBus, GameEvents } from 'src/events';
import { OtherActions } from 'shared/src/types';
import { GameState, useGameState } from './context';
import { ControlSection, ControlSectionDesktop, DesktopButton, DesktopControl, EndTurnButton, MobileLeftControl, MobileRightControl, SliderSection } from '../styled/controls';
import { useBreakpoints } from '../hooks/useBreakpoints';

const ControlPanel: React.FC = () => {
  const {isMobile, isLandscape} = useBreakpoints();

  const [angle, setAngle] = useState(90);
  const [power, setPower] = useState(50);
  const [isHyperspaceOn, setIsHyperspaceOn] = useState(false);
  const [shouldUpdateGame, setShouldUpdateGame] = useState(false);
  const { setGameState } = useGameState();

  useEffect(() => {
    if (shouldUpdateGame) {
      setShouldUpdateGame(false);
      gameBus.emit(GameEvents.ANGLE_POWER_UI, { angle, power });
    }
  }, [shouldUpdateGame]);

  useEffect(() => {
    gameBus.on(GameEvents.ANGLE_POWER_GAME, ({ angle, power }) => {
      setAngle(Math.trunc(angle));
      setPower(Math.trunc(power));
    });
    gameBus.on(GameEvents.OTHER_ACTION_GAME, (otherAction) => {
      if (otherAction === OtherActions.HYPERSPACE) {
        setIsHyperspaceOn(true);
      } else if (!otherAction) {
        setIsHyperspaceOn(false);
      }
    });
    return () => {
      gameBus.off(GameEvents.ANGLE_POWER_GAME);
      gameBus.off(GameEvents.OTHER_ACTION_GAME);
    };
  }, []);

  const adjustValue = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    delta: number,
    min: number,
    max: number,
  ) => {
    setter((prev) => Math.min(max, Math.max(min, prev + delta)));
    setShouldUpdateGame(true);
  };

  const emitAngle = (angle: number) => {
    setAngle(angle);
    gameBus.emit(GameEvents.ANGLE_POWER_UI, { angle, power });
  };

  const emitPower = (power: number) => {
    setPower(power);
    gameBus.emit(GameEvents.ANGLE_POWER_UI, { angle, power });
  };

  const endTurn = () => gameBus.emit(GameEvents.END_TURN);
  const toggleHyperspace = () => {
    if (isHyperspaceOn) {
      setIsHyperspaceOn(false);
      gameBus.emit(GameEvents.OTHER_ACTION_UI, OtherActions.NONE);
    } else {
      setIsHyperspaceOn(true);
      gameBus.emit(GameEvents.OTHER_ACTION_UI, OtherActions.HYPERSPACE);
    }
  };

  const backToMenu = () => {
    setGameState(GameState.MAIN_MENU);
  };

  if (isMobile && isLandscape) {
    return (
      <>
        <MobileLeftControl>
          <ControlSection>
            <EndTurnButton onClick={backToMenu}>
              <FaSignOutAlt size={15} />
            </EndTurnButton>
          </ControlSection>
          <ControlSection>
            <ValueLabel>{angle}°</ValueLabel>
            <FineButton
              onClick={() => adjustValue(setAngle, 1, -180, 180)}
              disabled={isHyperspaceOn}
            >
              +
            </FineButton>
          </ControlSection>
          <SliderSection>
            <StyledSlider
              $isVertical
              min={-180}
              max={180}
              value={Math.floor(angle)}
              onChange={(e) => emitAngle(Number(e.target.value))}
              disabled={isHyperspaceOn}
            />
          </SliderSection>
          <ControlSection>
            <FineButton
              onClick={() => adjustValue(setAngle, -1, -180, 180)}
              disabled={isHyperspaceOn}
            >
              -
            </FineButton>
          </ControlSection>
          <ControlSection>
            <EndTurnButton onClick={endTurn}>
              <FaCheck size={15} />
            </EndTurnButton>
          </ControlSection>
        </MobileLeftControl>

        <MobileRightControl>
          <ControlSection>
            <EndTurnButton onClick={() => toggleFullscreen()}>
              <FaExpand size={15} />
            </EndTurnButton>
          </ControlSection>
          <ControlSection>
            <ValueLabel>{power}%</ValueLabel>
            <FineButton
              onClick={() => adjustValue(setPower, -1, 20, 100)}
              disabled={isHyperspaceOn}
            >
              -
            </FineButton>
          </ControlSection>
          <SliderSection>
            <StyledSlider
              $isVertical
              min={20}
              max={100}
              value={Math.floor(power)}
              onChange={(e) => emitPower(Number(e.target.value))}
              disabled={isHyperspaceOn} 
            />
          </SliderSection>
          <ControlSection>
            <FineButton
              onClick={() => adjustValue(setPower, 1, 20, 100)}
              disabled={isHyperspaceOn}
            >
              +
            </FineButton>
          </ControlSection>
          <ControlSection>
            <EndTurnButton onClick={toggleHyperspace}>
              {isHyperspaceOn? <GiTransportationRings size={15} /> : <GiRingedBeam size={15} />}
            </EndTurnButton>
          </ControlSection>
        </MobileRightControl>
      </>
    )
  }

  return (
    <DesktopControl>
      <ControlSectionDesktop>
        <DesktopButton onClick={endTurn}>End Turn</DesktopButton>
      </ControlSectionDesktop>
      <ControlSectionDesktop>
        <DesktopButton onClick={toggleHyperspace}>
          Hyperspace{isHyperspaceOn ? ' [On] ' : ' [Off]'}
        </DesktopButton>
      </ControlSectionDesktop>
      <SliderContainer>
        <FineButton
          onClick={() => adjustValue(setAngle, -1, -180, 180)}
          disabled={isHyperspaceOn}
        >
          -
        </FineButton>
        <StyledSlider
          min={-180}
          max={180}
          value={Math.floor(angle)}
          onChange={(e) => emitAngle(Number(e.target.value))}
          disabled={isHyperspaceOn}
        />
        <FineButton
          onClick={() => adjustValue(setAngle, 1, -180, 180)}
          disabled={isHyperspaceOn}
        >
          +
        </FineButton>
        <ValueLabel>Angle: {angle}°</ValueLabel>
      </SliderContainer>
      <SliderContainer>
        <ValueLabel>Power: {power}%</ValueLabel>
        <FineButton
          onClick={() => adjustValue(setPower, -1, 20, 100)}
          disabled={isHyperspaceOn}
        >
          -
        </FineButton>
        <StyledSlider
          min={20}
          max={100}
          value={Math.floor(power)}
          onChange={(e) => emitPower(Number(e.target.value))}
          disabled={isHyperspaceOn}
        />
        <FineButton
          onClick={() => adjustValue(setPower, 1, 20, 100)}
          disabled={isHyperspaceOn}
        >
          +
        </FineButton>
      </SliderContainer>
      <ControlSectionDesktop>
        <MiniButton onClick={() => toggleFullscreen()}>
          <FaExpand />
        </MiniButton>
      </ControlSectionDesktop>
      <ControlSectionDesktop>
        <MiniButton onClick={backToMenu}>
          <FaSignOutAlt />
        </MiniButton>
      </ControlSectionDesktop>
    </DesktopControl>
  );
};

export default ControlPanel;
